"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Balancer } from "react-wrap-balancer";
import { twMerge } from "tailwind-merge";

import { useWatchlistContext } from "@/context/use-context/watchlist-provider/client";
import { OrientedUrl, urlIndexMap } from "@/data/constants/oriented";
import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { usePath } from "@/hooks/use-path";
import { Lang } from "@/i18n-config";
import emptyImage from "@/public/global/empty.svg";
import { getTranslations } from "@/utils/i18n/get-translations";

import { Backdrop } from "./backdrop/Backdrop";
import { SymbolHeader } from "./symbol-header/SymbolHeader";
import { SymbolList } from "./symbol-list/SymbolList";
import { WatchListHeader } from "./watch-list-header/WatchListHeader";

export type MyWatchListProps = {
  className?: string;
};

type OrientedIndex = 0 | 1 | 2 | 3 | 4;

export const MyWatchList = ({ className }: MyWatchListProps) => {
  const { lang } = useLang();
  const { isUnauthenticated } = useAuth();
  const { isOpen } = useWatchlistContext();

  const { lastRoute } = usePath();
  const [orientedIndex, setOrientedIndex] = useState<OrientedIndex>(0);

  useEffect(() => {
    setOrientedIndex(
      (urlIndexMap[lastRoute as OrientedUrl] ?? 0) as OrientedIndex
    );
  }, [lastRoute]);

  return (
    <>
      <aside
        className={twMerge(
          "sticky inset-y-0 right-0 z-30 overflow-y-auto bg-white transition-all",
          isOpen
            ? "w-[var(--watchlist-width)] border-l border-zinc-100"
            : "w-0",

          className
        )}
      >
        <div
          className={twMerge(
            "relative z-20 flex h-full flex-col gap-2 transition-all",
            !isOpen && "-mr-96 opacity-0"
          )}
        >
          <div className="mx-auto flex w-full flex-col bg-white shadow-sm">
            {/* Watchlist Options */}
            <WatchListHeader disabled={isUnauthenticated} />

            {/* SymbolList Options  */}
            {!isUnauthenticated && (
              <SymbolHeader
                orientedIndex={orientedIndex}
                setOrientedIndex={setOrientedIndex}
              />
            )}
          </div>
          {isUnauthenticated ? (
            <GuestView lang={lang} />
          ) : (
            <SymbolList orientedIndex={orientedIndex} />
          )}
        </div>
      </aside>
      {/* Backdrop */}
      {isOpen && <Backdrop />}
    </>
  );
};

const GuestView = ({ lang }: { lang: Lang }) => {
  const { t } = getTranslations(lang);
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center text-sm text-neutral-500">
      <Image src={emptyImage} alt="empty" />
      <p className="select-none px-2">
        <Balancer>
          <Link
            href={`/${lang}${links.authSignup}`}
            aria-label="sign up to create a watchlist"
            className="cursor-pointer font-medium text-[hsl(214,60%,60%)] underline underline-offset-2 transition-colors hover:text-[hsl(214,60%,40%)]"
          >
            {t.layout.watchlist.guest[0]}
          </Link>
          {t.layout.watchlist.guest[1]}
        </Balancer>
      </p>
    </div>
  );
};
